import React from 'react';

// Loading placeholders. Each one mirrors the real screen it stands in for - same paddings, same sizes, same rows - so
// nothing jumps when the real content arrives. Keep them in step with the screens they are named after.

const SAT = 'var(--sat-native, env(safe-area-inset-top, 0px))';
const SAB = 'var(--sab-native, env(safe-area-inset-bottom, 0px))';

export const SkeletonBlock: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className = '', style }) => (
  <div className={`skeleton-shimmer bg-white/5 rounded-lg ${className}`} style={style} />
);

export const SkeletonCircle: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`skeleton-shimmer bg-white/5 rounded-full ${className}`} />
);

// --- Home feed (sections/Feed.tsx + components/PostCard.tsx) ---------------------------------------------------------------
const PostCardSkeleton: React.FC = () => (
  <div className="mx-auto mb-4 w-full max-w-2xl">
    {/* media shell: the card's picture (0.8 ratio until the real one is known) with the rider chip top-left */}
    <div className="skeleton-shimmer relative overflow-hidden rounded-2xl bg-white/5" style={{ aspectRatio: '0.8' }}>
      <div className="absolute left-2.5 top-2.5 flex items-center gap-2.5 rounded-2xl bg-black/30 p-1.5 pr-4">
        <div className="h-10 w-10 rounded-xl bg-white/10" />
        <div className="space-y-1.5">
          <div className="h-3 w-20 rounded bg-white/10" />
          <div className="h-2.5 w-12 rounded bg-white/10" />
        </div>
      </div>
    </div>
    <div className="space-y-2 px-4 pb-3 pt-3">
      {/* like / react / comments on the left, share / settings / save on the right */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex h-12 items-center gap-1.5 px-2">
              <SkeletonCircle className="h-9 w-9" />
              <SkeletonBlock className="h-4 w-4" />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-0.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex h-10 w-10 items-center justify-center"><SkeletonCircle className="h-6 w-6" /></div>
          ))}
        </div>
      </div>
      <SkeletonBlock className="h-5 w-4/5" />
    </div>
  </div>
);

export const FeedSkeleton: React.FC = () => (
  <div className="min-h-screen bg-app-backdrop pb-32">
    <div className="relative flex items-start" style={{ minHeight: `calc(${SAT} + 12.5rem)`, paddingTop: `calc(${SAT} + 0.625rem)` }}>
      {/* the story tiles: 128 x 10.25rem, 8px apart, leaving room for the side tab */}
      <div className="w-full overflow-hidden pl-2 pr-[6.25rem]">
        <div className="flex gap-2">
          {[0, 1, 2, 3].map((i) => (
            <SkeletonBlock key={i} className="h-[10.25rem] w-32 shrink-0 rounded-3xl" />
          ))}
        </div>
      </div>
      {/* the side tab: notifications logo box + upload button */}
      <div className="absolute right-0 top-0 flex flex-col items-center gap-3 px-3 pb-3.5" style={{ paddingTop: `calc(${SAT} + 0.625rem)` }}>
        <SkeletonBlock className="h-[76px] w-[76px] rounded-2xl" />
        <SkeletonBlock className="h-[76px] w-[76px] rounded-2xl" />
      </div>
    </div>
    <PostCardSkeleton />
    <PostCardSkeleton />
  </div>
);

// --- Profile (components/ProfileOverview.tsx + the posts grid) -------------------------------------------------------------
export const ProfileSkeleton: React.FC = () => (
  <div className="min-h-screen bg-app-backdrop">
    <div className="relative w-full overflow-hidden" style={{ height: `calc(260px + ${SAT})` }}>
      <SkeletonBlock className="h-full w-full rounded-none" />
      <div className="absolute bottom-3 left-4 h-28 w-28 rounded-[24px] border-[5px] border-[#101113] bg-[#101113] p-[4px] sm:left-6">
        <SkeletonBlock className="h-full w-full rounded-[16px] !bg-white/10" />
      </div>
    </div>
    <section className="mx-auto max-w-3xl px-4 pb-4 sm:px-6">
      <SkeletonBlock className="mt-4 h-8 w-52" />
      <SkeletonBlock className="mt-1.5 h-5 w-28" />
      <div className="mt-3 space-y-2">
        <SkeletonBlock className="h-3.5 w-full" />
        <SkeletonBlock className="h-3.5 w-3/4" />
      </div>
      <div className="my-2 flex items-center gap-x-6">
        {[0, 1, 2].map((i) => <SkeletonBlock key={i} className="my-3 h-6 w-20" />)}
      </div>
      <div className="flex gap-2">
        <SkeletonBlock className="h-11 flex-1 rounded-xl" />
        <SkeletonBlock className="h-11 flex-1 rounded-xl" />
      </div>
    </section>
    <div className="mx-auto w-full max-w-3xl px-4 sm:px-6">
      <div className="flex border-b border-white/10">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex min-h-12 flex-1 items-center justify-center"><SkeletonBlock className="h-4 w-14" /></div>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-1.5 pt-4">
        {Array.from({ length: 8 }).map((_, i) => <SkeletonBlock key={i} className="aspect-square rounded-lg" />)}
      </div>
    </div>
  </div>
);

// --- Discover (sections/Discover.tsx) ---------------------------------------------------------------------------------------
export const DiscoverSkeleton: React.FC = () => (
  <div className="min-h-screen bg-app-backdrop p-2 pb-32" style={{ paddingTop: `calc(${SAT} + 1rem)` }}>
    <header className="mb-4 px-2">
      <SkeletonBlock className="h-9 w-44" />
      <div className="mt-2 flex items-stretch gap-2">
        <SkeletonBlock className="h-[2.75rem] min-w-0 flex-1 rounded-lg" />
        <SkeletonBlock className="h-[2.75rem] w-[7.5rem] shrink-0 rounded-lg" />
      </div>
    </header>
    <div className="grid grid-cols-4 gap-1">
      {Array.from({ length: 24 }).map((_, i) => <SkeletonBlock key={i} className="aspect-square rounded-none" />)}
    </div>
  </div>
);

// Kept for any screen that just needs a plain square grid.
export const GridSkeleton: React.FC<{ tiles?: number }> = ({ tiles = 9 }) => (
  <div className="min-h-screen bg-app-backdrop p-2 pb-32" style={{ paddingTop: `calc(${SAT} + 1rem)` }}>
    <div className="grid grid-cols-3 gap-1.5">
      {Array.from({ length: tiles }).map((_, i) => <SkeletonBlock key={i} className="aspect-square rounded-lg" />)}
    </div>
  </div>
);

// --- Buy & Sell (sections/Marketplace.tsx) ---------------------------------------------------------------------------------
export const MarketplaceSkeleton: React.FC = () => (
  <div className="mx-auto w-full max-w-[820px] px-4" style={{ paddingTop: `calc(${SAT} + 0.75rem)` }}>
    <div className="flex items-center justify-between gap-3">
      <SkeletonBlock className="h-8 w-36" />
      <div className="flex gap-1.5">
        <SkeletonBlock className="h-10 w-28 rounded-xl" />
        <SkeletonBlock className="h-10 w-20 rounded-xl" />
      </div>
    </div>
    <div className="mt-3 grid grid-cols-3 border-b border-white/10">
      {[0, 1, 2].map((i) => <div key={i} className="flex justify-center px-3 py-3"><SkeletonBlock className="h-4 w-16" /></div>)}
    </div>
    <SkeletonBlock className="mt-3 h-[2.75rem] w-full rounded-xl" />
    <SkeletonBlock className="mt-3 h-[4.25rem] w-full rounded-xl" />
    <SkeletonBlock className="mt-3 h-[4.25rem] w-full rounded-xl" />
    <div className="mt-2 flex items-center justify-between">
      <SkeletonBlock className="h-7 w-20 rounded-full" />
      <SkeletonBlock className="h-7 w-28 rounded-lg" />
    </div>
    <SkeletonBlock className="mt-3 h-3.5 w-20" />
    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-5 pb-32 sm:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i}>
          <SkeletonBlock className="aspect-square w-full rounded-xl" />
          <SkeletonBlock className="mt-2 h-4 w-4/5" />
          <SkeletonBlock className="mt-1.5 h-3 w-1/2" />
        </div>
      ))}
    </div>
  </div>
);

// --- Messages list (sections/Messenger.tsx + components/ChatListItem.tsx) --------------------------------------------------
export const MessengerSkeleton: React.FC = () => (
  <div className="min-h-screen bg-app-backdrop p-3 pb-32" style={{ paddingTop: `calc(${SAT} + 1.25rem)` }}>
    <div className="mb-4 flex min-h-[6.4rem] gap-4 overflow-hidden px-1">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex w-[4.75rem] shrink-0 flex-col items-center gap-1.5">
          <SkeletonBlock className="h-[4.5rem] w-[4.5rem] rounded-2xl" />
          <SkeletonBlock className="h-3.5 w-14" />
        </div>
      ))}
    </div>
    <div className="mb-4 flex items-stretch gap-2 px-1">
      <SkeletonBlock className="h-[2.75rem] min-w-0 flex-1 rounded-lg" />
      <SkeletonBlock className="h-[2.75rem] w-[7rem] shrink-0 rounded-lg" />
    </div>
    <div className="space-y-2.5 px-1">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3.5 rounded-3xl border border-white/[0.06] bg-[#141414] p-3.5">
          <SkeletonBlock className="h-16 w-16 shrink-0 rounded-2xl" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <SkeletonBlock className="h-4 w-32" />
              <SkeletonBlock className="h-3 w-10" />
            </div>
            <SkeletonBlock className="mt-2.5 h-3.5 w-4/5" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// --- Notifications (sections/Notifications.tsx) -----------------------------------------------------------------------------
export const NotificationsSkeleton: React.FC = () => (
  <div className="min-h-screen space-y-4 bg-black p-4 pb-32" style={{ paddingTop: `calc(${SAT} + 1rem)` }}>
    <div className="flex items-center gap-3">
      <SkeletonBlock className="h-11 w-11 rounded-2xl" />
      <SkeletonBlock className="h-7 w-44" />
    </div>
    <div className="grid grid-cols-3 gap-2.5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-col items-center gap-2 rounded-2xl border-2 border-white/10 px-2 py-4">
          <SkeletonCircle className="h-10 w-10" />
          <SkeletonBlock className="h-5 w-16" />
        </div>
      ))}
    </div>
    <div className="space-y-2.5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-2xl bg-[#1c1c1c] p-3.5">
          <SkeletonCircle className="h-11 w-11 shrink-0" />
          <div className="min-w-0 flex-1 space-y-2">
            <SkeletonBlock className="h-3.5 w-3/4" />
            <SkeletonBlock className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// --- A chat (sections/ChatDetail.tsx): header, messages, message bar ---------------------------------------------------------
export const ChatSkeleton: React.FC<{ group?: boolean }> = ({ group = false }) => (
  <div className="flex h-full flex-col bg-app-backdrop">
    <div className="flex flex-col gap-2 border-b border-gray-700 bg-app-overlay p-3" style={{ paddingTop: `calc(${SAT} + 0.75rem)` }}>
      {group ? (
        <>
          <div className="flex items-center gap-2">
            <SkeletonBlock className="h-12 w-12 shrink-0 rounded-full" />
            <SkeletonBlock className="h-14 w-14 shrink-0 rounded-xl" />
            <div className="min-w-0 flex-1 space-y-2">
              <SkeletonBlock className="h-5 w-3/4" />
              <SkeletonBlock className="h-3.5 w-1/3" />
            </div>
            <div className="flex shrink-0 gap-1.5">
              {[0, 1, 2].map((i) => <SkeletonCircle key={i} className="h-11 w-11" />)}
            </div>
          </div>
          <SkeletonBlock className="h-11 w-full rounded-xl" />
        </>
      ) : (
        <div className="flex items-center">
          <SkeletonBlock className="-ml-1 mr-1 h-12 w-12 shrink-0 rounded-full" />
          <SkeletonBlock className="h-[5.25rem] w-[5.25rem] shrink-0 rounded-2xl" />
          <div className="ml-3.5 min-w-0 flex-1 space-y-2.5">
            <SkeletonBlock className="h-7 w-2/3" />
            <SkeletonBlock className="h-5 w-1/3" />
          </div>
          <div className="ml-2 flex shrink-0 gap-3.5">
            <SkeletonCircle className="h-14 w-14" />
            <SkeletonCircle className="h-14 w-14" />
          </div>
        </div>
      )}
    </div>
    <div className="flex flex-1 flex-col justify-end space-y-3 overflow-hidden p-4">
      {[
        { align: 'start', w: 'w-44', h: 'h-11' },
        { align: 'end', w: 'w-32', h: 'h-11' },
        { align: 'start', w: 'w-56', h: 'h-[4.25rem]' },
        { align: 'start', w: 'w-28', h: 'h-11' },
        { align: 'end', w: 'w-40', h: 'h-11' },
      ].map((bubble, i) => (
        <div key={i} className={`flex ${bubble.align === 'end' ? 'justify-end' : 'justify-start'}`}>
          <SkeletonBlock className={`${bubble.w} ${bubble.h} rounded-2xl`} />
        </div>
      ))}
    </div>
    <div className="border-t border-gray-700 bg-app-surface px-3 pt-2" style={{ paddingBottom: `max(0.5rem, calc(${SAB} + 0.5rem))` }}>
      <div className="flex items-center gap-2">
        <SkeletonBlock className="h-[3.25rem] min-w-0 flex-1 rounded-full" />
        {[0, 1, 2, 3].map((i) => <SkeletonCircle key={i} className="h-12 w-12 shrink-0" />)}
      </div>
    </div>
  </div>
);

// --- Create a chat / any plain list of people (sections/CreateGroupChat.tsx) -----------------------------------------------
export const ListSkeleton: React.FC<{ rows?: number }> = ({ rows = 8 }) => (
  <div className="min-h-screen bg-app-backdrop p-3 pb-32" style={{ paddingTop: `calc(${SAT} + 1rem)` }}>
    <div className="mb-4 flex items-center gap-3 px-1">
      <SkeletonBlock className="h-11 w-11 rounded-full" />
      <SkeletonBlock className="h-7 w-40" />
    </div>
    <SkeletonBlock className="mb-4 h-[2.75rem] w-full rounded-lg" />
    <div className="space-y-1">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-2.5">
          <SkeletonBlock className="h-12 w-12 shrink-0 rounded-xl" />
          <div className="flex-1 space-y-2">
            <SkeletonBlock className="h-4 w-1/2" />
            <SkeletonBlock className="h-3 w-1/3" />
          </div>
          <SkeletonCircle className="h-6 w-6 shrink-0" />
        </div>
      ))}
    </div>
  </div>
);

// --- Zips (sections/Zips.tsx + components/ZipCard.tsx): full-screen clip with seek bar, rider chip, caption and button rail ---
export const ZipsSkeleton: React.FC = () => (
  <div className="relative h-full w-full bg-black">
    <SkeletonBlock className="absolute inset-0 rounded-none !bg-white/[0.04]" />
    <div className="absolute inset-x-0 flex h-5 items-center px-3" style={{ top: SAT }}>
      <div className="h-[7px] w-full rounded-full bg-white/20" />
    </div>
    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 px-4" style={{ paddingBottom: `calc(${SAB} + 96px)` }}>
      <div className="w-[72%]">
        <div className="mb-3 flex w-fit items-center gap-2.5 rounded-2xl border border-white/10 bg-white/10 py-1.5 pl-1.5 pr-4">
          <SkeletonBlock className="h-10 w-10 rounded-xl !bg-white/15" />
          <div className="space-y-1.5">
            <SkeletonBlock className="h-3.5 w-24 !bg-white/15" />
            <SkeletonBlock className="h-3 w-16 !bg-white/15" />
          </div>
        </div>
        <SkeletonBlock className="mb-2 h-4 w-full !bg-white/15" />
        <SkeletonBlock className="h-4 w-2/3 !bg-white/15" />
      </div>
      <div className="mb-2 flex flex-col items-center gap-6">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex flex-col items-center gap-1">
            <SkeletonCircle className="h-10 w-10 !bg-white/15" />
            <SkeletonBlock className="h-2.5 w-7 !bg-white/15" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// --- Spot map (sections/SpotMap.tsx): dark map with the search bar, the two button rows and the controls bottom-right --------
export const MapSkeleton: React.FC = () => (
  <div className="relative h-full w-full overflow-hidden bg-[#1b1c1f]">
    <div className="absolute left-4 right-4 z-10 flex flex-col items-center gap-2" style={{ top: `calc(${SAT} + 1rem)` }}>
      <SkeletonBlock className="h-[3.25rem] w-full max-w-md rounded-xl !bg-white/20" />
      <div className="grid w-full max-w-md grid-cols-2 gap-2">
        <SkeletonBlock className="h-12 rounded-xl !bg-white/15" />
        <SkeletonBlock className="h-12 rounded-xl !bg-white/15" />
        <SkeletonBlock className="h-12 rounded-xl !bg-white/15" />
        <SkeletonBlock className="h-12 rounded-xl !bg-white/15" />
      </div>
    </div>
    <div className="absolute right-4 z-10 flex flex-col items-end gap-2" style={{ bottom: `calc(${SAB} + 4.9rem)` }}>
      <SkeletonCircle className="h-11 w-11 !bg-white/15" />
      <SkeletonBlock className="h-[2.6rem] w-48 rounded-xl !bg-white/15" />
      <SkeletonBlock className="h-[2.75rem] w-48 rounded-xl !bg-white/15" />
    </div>
  </div>
);

// Plain full-screen placeholder for anything that has no layout of its own.
export const FullScreenSkeleton: React.FC = () => (
  <div className="h-full w-full bg-app-backdrop">
    <SkeletonBlock className="h-full w-full rounded-none" />
  </div>
);
